import React, { useMemo, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { toast } from "sonner";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";
import { useRoles } from "../../../../shared/api/hooks/useRoles";
import { useAdmins } from "../../../../shared/api/hooks/useAdmins";

interface EscalateIssueModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    escalateType: "role" | "member";
    targetName: string;
    roleId?: string;
    adminId?: string;
    note: string;
  }) => void;
}

export const EscalateIssueModal: React.FC<EscalateIssueModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const [escalateType, setEscalateType] = useState<"role" | "member">("role");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedRoleName, setSelectedRoleName] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const [note, setNote] = useState("");

  const { roles, isLoading: isLoadingRoles } = useRoles();
  const { admins, isLoading: isLoadingAdmins } = useAdmins({
    isProfileCompleted: true,
  });

  const roleList = useMemo(() => {
    if (roles && roles.length > 0) {
      return roles.map((r) => ({ id: r.id, name: r.name }));
    }
    return [];
  }, [roles]);

  const memberList = useMemo(() => {
    if (admins && admins.length > 0) {
      return admins.map((a) => ({
        id: a._id,
        name: `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.email,
        email: a.email,
      }));
    }
    return [];
  }, [admins]);

  const handleClose = () => {
    onOpenChange(false);
    setSelectedRoleId("");
    setSelectedRoleName("");
    setSelectedMemberId("");
    setSelectedMemberName("");
    setNote("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const targetName =
      escalateType === "role" ? selectedRoleName : selectedMemberName;

    if (!targetName) {
      toast.error(
        `Please select a ${escalateType === "role" ? "role" : "team member"} to escalate to`
      );
      return;
    }

    onSubmit({
      escalateType,
      targetName,
      roleId: escalateType === "role" ? selectedRoleId : undefined,
      adminId: escalateType === "member" ? selectedMemberId : undefined,
      note,
    });
    handleClose();
  };

  const hasSelection =
    escalateType === "role" ? Boolean(selectedRoleId) : Boolean(selectedMemberId);

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[520px] bg-white rounded-[24px]"
    >
      <ModalHeader className="items-center justify-between pb-3 border-b border-[#EBEDF9]">
        <ModalTitle className="text-[20px] font-[600] text-[#161827]">
          Escalate to...
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          onClick={handleClose}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] transition-colors"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="flex flex-col gap-6 py-6">
          {/* Escalate to... Radio */}
          <div className="flex flex-col gap-2.5">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Escalate to...
            </p>
            <div className="flex items-center gap-8">
              {(["role", "member"] as const).map((type) => {
                const isActive = escalateType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setEscalateType(type)}
                    className="inline-flex items-center gap-3 text-[14px] font-[450] text-[#2C2F44] transition-colors focus:outline-none"
                  >
                    <span
                      className={`grid h-5 w-5 place-items-center rounded-full border transition-colors ${
                        isActive ? "border-[#6F00FF]" : "border-[#C7CEE4]"
                      }`}
                    >
                      {isActive ? (
                        <span className="h-2.5 w-2.5 rounded-full bg-[#6F00FF]" />
                      ) : null}
                    </span>
                    <span className="capitalize">{type}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Role / Member Selection */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44] capitalize">
              {escalateType}
            </p>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex h-12 w-full items-center justify-between rounded-[10px] bg-[#E9ECF8] px-4 text-left transition-colors hover:bg-[#E2E6F5] focus:outline-none"
                >
                  <span
                    className={`text-[14px] font-[400] truncate ${
                      (escalateType === "role" ? selectedRoleName : selectedMemberName)
                        ? "text-[#161827]"
                        : "text-[#737AA8]"
                    }`}
                  >
                    {escalateType === "role"
                      ? selectedRoleName || (isLoadingRoles ? "Loading roles..." : "Select role")
                      : selectedMemberName || (isLoadingAdmins ? "Loading members..." : "Select member")}
                  </span>
                  <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={6}
                  className="z-[125] max-h-[240px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[12px] border border-[#E1E6FF] bg-white p-1.5 shadow-lg"
                >
                  {escalateType === "role" ? (
                    roleList.length ? (
                      roleList.map((r) => {
                        const isSelected = r.name === selectedRoleName;
                        return (
                          <DropdownMenu.Item
                            key={r.id}
                            onSelect={() => {
                              setSelectedRoleId(r.id);
                              setSelectedRoleName(r.name);
                            }}
                            className={`flex items-center cursor-pointer rounded-[8px] px-3.5 py-2.5 text-[14px] outline-none transition-colors focus:bg-[#F5F6FD] ${
                              isSelected
                                ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                                : "text-[#383B56]"
                            }`}
                          >
                            <span className="truncate">{r.name}</span>
                          </DropdownMenu.Item>
                        );
                      })
                    ) : (
                      <div className="px-3.5 py-2 text-[13px] text-[#6F739B]">
                        {isLoadingRoles ? "Loading roles..." : "No roles found."}
                      </div>
                    )
                  ) : memberList.length ? (
                    memberList.map((m) => {
                      const isSelected = m.name === selectedMemberName;
                      return (
                        <DropdownMenu.Item
                          key={m.id}
                          onSelect={() => {
                            setSelectedMemberId(m.id);
                            setSelectedMemberName(m.name);
                          }}
                          className={`cursor-pointer rounded-[8px] px-3.5 py-2 outline-none transition-colors focus:bg-[#F5F6FD] ${
                            isSelected
                              ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                              : "text-[#383B56]"
                          }`}
                        >
                          <div className="text-[14px] font-[500] text-[#161827]">
                            {m.name}
                          </div>
                          {m.email && (
                            <div className="text-[12px] text-[#6F739B]">
                              {m.email}
                            </div>
                          )}
                        </DropdownMenu.Item>
                      );
                    })
                  ) : (
                    <div className="px-3.5 py-2 text-[13px] text-[#6F739B]">
                      {isLoadingAdmins ? "Loading members..." : "No members found."}
                    </div>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Note • Optional */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Note <span className="font-normal italic text-[#6F739B]">• Optional</span>
            </p>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add notes"
              className="min-h-[100px] w-full resize-none rounded-[10px] bg-[#E9ECF8] p-4 text-[14px] text-[#161827] placeholder:text-[#9A9CC0] focus:outline-none focus:ring-2 focus:ring-[#D9DEFF]"
            />
          </div>
        </ModalBody>

        <ModalFooter className="flex items-center justify-end border-t border-[#EBEDF9] px-6 py-4">
          <Button
            type="submit"
            disabled={!hasSelection}
            className="h-11 rounded-[10px] px-6 text-[15px] font-[500] bg-[#161827] text-white hover:bg-[#2B2F49] disabled:bg-[#9A9CC0] disabled:opacity-100 disabled:cursor-not-allowed transition-colors"
          >
            Escalate
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
